import * as React from 'react';
import { FormGroup } from 'reactstrap';
import i18n from '@s20/i18n';
import { httpClient } from '@s20/services/httpClient';
import authService from '../api-authorization/AuthorizeService';
import CommonContext, { ApiRoutes } from '../Common';
import { FormLabel, Textarea } from '../common/forms/FormElements';
import SignatureButton from '../common/forms/SignatureButton';
import SignatureImage from '../common/forms/SignatureImage';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { TimesheetSignatureType } from './ClientReview';

export const NoReasonInput = (props) => {
    const {
        formValidated, tenantSettings, review, missingSignatureReasons,
        onMissingSigReasonChanged, onChangeNotes,
    } = { ...props };

    // If the tenant setting is enabled, show dropdown with prdefined reasons.
    if (!review.isComplete && tenantSettings.enablePredefinedTimesheetMissingSigReason) {
        return (
            <ValidatedSelect
                id="noSigReasons"
                name="review.missingSigReasonId"
                label={i18n.t('timesheet:missingSignatureReason')}
                required={!review.isComplete}
                isDisabled={!!review.isComplete}
                options={missingSignatureReasons}
                value={(missingSignatureReasons ?? []).find((s) => s.value === review.missingSigReasonId) ?? ''}
                onChange={onMissingSigReasonChanged}
                error={i18n.t('timesheet:missingSignatureReasonRequired')}
            />
        );
    }

    if (!review.isComplete && !tenantSettings.enablePredefinedTimesheetMissingSigReason) {
        return (
            <Textarea
                id="notes"
                name="review.notes"
                label={i18n.t('timesheet:missingSignatureReason')}
                value={review.notes ?? ''}
                onChange={onChangeNotes}
                placeholder={i18n.t('timesheet:missingSignatureNotesPlaceholder')}
                maxLength="500"
                rows="5"
                required
                disabled={!!review.isComplete}
                error={!!formValidated && i18n.t('timesheet:missingSignatureNotesRequired')}
            />
        );
    }

    return (
        <>
            <FormLabel
                label={i18n.t('timesheet:missingSignatureReason')}
            />

            <span className="form-control-plaintext">{review.missingSigReason ?? review.notes ?? ''}</span>
        </>
    );
};

/**
* @augments {Component<{
 * review: object,
 * onSignatureTypeChanged:function,
 * onMissingSigReasonChanged:function,
 * onChangeNotes:function,
 * onSigSavedCallback:function,
 * tenantSettings:object,
 * formValidated:boolean,
 * sigIsComplete:boolean *
 * >}
*/
export class ClientTimesheetSignature extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            signatureTypes: [],
            missingSignatureReasons: [],
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const [signatureTypes, missingSigReasons] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.clientSignatureTypes()),
                httpClient.js(ApiRoutes.timesheetReview.missingSignatureReasons()),
            ]);

            this.setState({
                signatureTypes,
                missingSignatureReasons: missingSigReasons,
            });
        }
    }

    render() {
        const {
            review, onSignatureTypeChanged, onMissingSigReasonChanged, onChangeNotes,
            onSigSavedCallback, tenantSettings, formValidated, sigIsComplete,
        } = { ...this.props };

        const { signatureTypes, missingSignatureReasons } = { ...this.state };

        return (
            <>
                <ValidatedSelect
                    id="clientSignatureTypeId"
                    name="review.signatureTypeId"
                    required
                    label={i18n.t('timesheet:signatureTypeColon')}
                    isDisabled={!!review.isComplete}
                    options={signatureTypes}
                    value={(signatureTypes ?? []).find((s) => s.value === review.signatureTypeId) ?? ''}
                    onChange={onSignatureTypeChanged}
                    error={i18n.t('timesheet:clientSignatureTypeRequired')}
                    hideClear
                />

                {
                    (review ?? {}).signatureTypeId === TimesheetSignatureType.ClientNoSignature
                    && (
                        <NoReasonInput
                            tenantSettings={tenantSettings}
                            review={review}
                            formValidated={formValidated}
                            missingSignatureReasons={missingSignatureReasons}
                            onMissingSigReasonChanged={onMissingSigReasonChanged}
                            onChangeNotes={onChangeNotes}
                        />
                    )
                }
                {
                    (review ?? {}).signatureTypeId !== TimesheetSignatureType.ClientNoSignature
                    && (
                        <>
                            {
                                (!!review.isComplete || !!sigIsComplete)
                                && (
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="sigPreview"
                                            label={i18n.t('timesheet:clientSignatureLabel')}
                                        />
                                        <SignatureImage
                                            alt={i18n.t('timesheet:signaturePreview')}
                                            id="sigPreview"
                                            style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ccc' }}
                                            src={review.reviewSignature.signatureData}
                                        />
                                    </FormGroup>
                                )
                            }
                            {
                                (!review.isComplete && !sigIsComplete)
                                && (
                                    <SignatureButton
                                        id="clientReviewButton"
                                        onSigSavedCallback={onSigSavedCallback}
                                        sigData={review?.reuseClientSignature ? review?.reviewSignature?.signatureData : null}
                                        reviewId={review?.id}
                                    />
                                )
                            }
                        </>
                    )
                }
            </>
        );
    }
}
